import { Events, SkapaFocusLockEvent } from './utils/focus-lock-event.mjs';
import { getFirstFocusable, setDocumentListeners, getNodeHost, getComposedPathForLeaf, getActiveLeaf, } from './utils/helpers.mjs';
import { createCatchers, removeCatchers, CATCHER_ATTRIBUTE_NAME, } from './utils/focus-catcher.mjs';
export { Events, SkapaFocusLockEvent };
// Since the effects of SkapaFocusLock are so very intrusive, it will have to be manually and explicitly activated.
export class SkapaFocusLock extends EventTarget {
    constructor(container) {
        super();
        // Controls if any `data-autofocus` flagged nested focusable element should be focused on lock.
        this.autoFocus = false;
        // Internal feature control - Sets the focus lock feature into a temporary disabled state to prevent race conditions when multiple active focus regions are activated.
        this.passive = false;
        // Controls if pointer events outside the focus regions should be cancelled when the compoennt is on.
        this.capturePointerEvents = true;
        // Controls if the focus should be returned after deactivation
        this.autoFocusReturn = true;
        this.__active = false;
        this.__disableKeyConfig = {
            key: 'Escape',
            shift: false,
            ctrl: false,
        };
        this.__shiftKeyDown = false;
        this.handleKeyboard = (ev) => {
            if (this.passive)
                return;
            // Self disable if disable hotkey is pressed.
            if (ev.type === 'keydown' &&
                typeof ev.key === 'string' &&
                ev.key.toLowerCase() === this.__disableKeyConfig.key.toLowerCase()) {
                const { shift, ctrl } = this.__disableKeyConfig;
                if (shift === ev.shiftKey && ctrl === ev.ctrlKey) {
                    // Disable hotkey pressed.
                    this.active = false;
                    // should dispatch an event to let the implementer know what happened?
                    this.dispatchEvent(new SkapaFocusLockEvent(Events.Escape, this));
                    return;
                }
            }
            if (ev.key !== 'Shift')
                return;
            this.__shiftKeyDown = ev.type === 'keydown';
        };
        this.disconnectedLockWrapper = () => {
            const isDisconnected = !(this.container && this.container.isConnected);
            if (isDisconnected)
                console.warn('Focus lock feature was not disabled before removing the container from the DOM tree.');
            return isDisconnected;
        };
        this.handlePointer = (ev) => {
            if (this.passive || !this.capturePointerEvents)
                return;
            if (this.disconnectedLockWrapper())
                return;
            const cPath = ev.composedPath();
            if (!(cPath.includes(this.container) || // this can save some manual calculation time
                cPath.find((el) => Element.prototype.isPrototypeOf(el) &&
                    (el.classList.contains('skapa-focus-portal') ||
                        (el.getAttribute('role') || '').includes('dialog'))) ||
                this.isElementWithin(cPath[0]))) {
                // then don't click or touch scroll
                if (ev.cancelable)
                    ev.preventDefault();
                if (ev.type === 'click')
                    ev.stopImmediatePropagation();
            }
        };
        this.enableLock = () => {
            // perhaps some safety cleanup?
            this.container.addEventListener('focusout', this.handleFocus);
            setDocumentListeners(true);
            createCatchers(this);
            // The event dispatch should come after setting the document listeners and before the capturing of the focus.
            document.dispatchEvent(new SkapaFocusLockEvent(Events.Enable, this));
            this.captureFocus();
        };
        this.disableLock = () => {
            removeCatchers(this);
            setDocumentListeners(false);
            this.container.removeEventListener('focusout', this.handleFocus);
            this.returnFocus();
            // The event dispatch should come before removing the document listeners and after the returning of the focus.
            document.dispatchEvent(new SkapaFocusLockEvent(Events.Disable, this));
        };
        /** Capture and return initial focus */
        this.captureFocus = () => {
            if (document.activeElement)
                this._activeElementBeforeFocusLock = getActiveLeaf();
            // Init focus
            // Implement these for retrocompatibility with the react-focus-trap package: https://github.com/theKashey/focus-lock#declarative-control
            // TODO This does not search for nested focusable elements.
            const autofocus = this.autoFocus &&
                this.container.querySelector('*[data-autofocus]:not([tabindex="-1"]):not(:disabled)');
            const focusTarget = getFirstFocusable(autofocus
                ? [autofocus]
                : [this.container, ...Array.from(this.container.querySelectorAll('*'))], false, this.container);
            if (focusTarget)
                focusControl(focusTarget);
            // Capturing with a slight delay for better screenreader UX.
            else
                this.noFocusTargetException();
        };
        this.returnFocus = () => {
            if (!this.autoFocusReturn)
                return;
            if (this._activeElementBeforeFocusLock &&
                this._activeElementBeforeFocusLock.isConnected) {
                focusControl(this._activeElementBeforeFocusLock); // Returning with a slight delay for better screenreader UX.
            }
        };
        /** Internal - handle focus event callback. */
        this.handleFocus = (ev) => {
            if (this.passive)
                return;
            // If the container has been removed from the DOM tree.
            if (!this.container.isConnected)
                return;
            // Couple of check if the event is an aritifical event that does not follow the relevant API standards.
            const relatedTarget = ev.relatedTarget &&
                'nodeType' in ev.relatedTarget &&
                ev.relatedTarget.nodeType === 1
                ? ev.relatedTarget
                : null;
            if (!relatedTarget || relatedTarget.hasAttribute(CATCHER_ATTRIBUTE_NAME))
                return;
            if (!this.isElementWithin(relatedTarget) // If the newly focused element is not a descendant of the focus-lock region.
            ) {
                // then recapture focus
                ev.preventDefault();
                const dispatcher = ev.composedPath()[0];
                const focusTarget = dispatcher ||
                    getFirstFocusable([this.container, ...Array.from(this.container.querySelectorAll('*'))], this.__shiftKeyDown, this.container);
                if (focusTarget)
                    focusTarget.focus();
                else
                    this.noFocusTargetException();
            }
        };
        this.isElementWithin = (el, container = this.container) => {
            var _a;
            return Boolean(el &&
                (container.contains(el) ||
                    (container.shadowRoot && container.shadowRoot.contains(el)) ||
                    ((_a = getNodeHost(container)) === null || _a === void 0 ? void 0 : _a.contains(el)) ||
                    getComposedPathForLeaf(el).includes(container)));
        };
        this.noFocusTargetException = () => {
            // Nothing to focus on. Disable?
            console.warn(`Warning! Skapa Focus-lock found no focusable elements on ${this.container}.\nFocus lock remains disabled.`);
            this.disableLock();
        };
        this.container = container;
    }
    get active() {
        return this.__active;
    }
    // Controls the focus lock feature
    set active(value) {
        if (this.__active === value)
            return; // prevent multiple initialisations and event dispatches.
        this.__active = value;
        if (value)
            this.enableLock();
        else
            this.disableLock();
    }
    get disableKey() {
        return this.__disableKeyConfig;
    }
    // Override the hotkey for disabling the lock.
    setDisableKey(config) {
        this.__disableKeyConfig = Object.assign({ key: 'Escape', shift: false, ctrl: false }, config);
    }
}
let focusControlTaskID = -1;
function focusControl(target) {
    clearTimeout(focusControlTaskID);
    if (typeof window === 'undefined')
        return;
    focusControlTaskID = window.setTimeout(() => {
        if (target && target.isConnected)
            target.focus();
    }, 50); // Slight delay improves screen reader compatiblity.
}
